/**
 * Backwards-compatible facade over the typed event registry at `./events/`.
 *
 * NEW CODE: import event definitions from `@repo/statsig/events/<surface>`
 * (e.g. `subscriptionCreated` from `./events/subscription`) and use the
 * typed `logEvent` / `logStatsigEvent` overloads. The registry validates
 * fire-site metadata at compile time + runtime.
 *
 * EXISTING CODE: continues to import `STATSIG_EVENTS`, `CTA_CLICKED_VALUES`,
 * etc. from `@repo/statsig` and these resolve to the same string constants
 * as before. The `satisfies` clauses below ensure every constant maps to a
 * registered event so the registry stays the single source of truth.
 *
 * See `packages/statsig/TAXONOMY.md` for conventions, versioning policy,
 * and recipes for adding/changing events.
 */
import type { RegisteredEventName } from "./events/registry";

export type { RegisteredEventName } from "./events/registry";
export {
  EVENT_REGISTRY,
  getEventDefinition,
  type EventValueFor,
  type EventMetadataFor,
  type EventDefinitionFor,
  type EventRegistry,
} from "./events/registry";

export {
  CTA_CLICKED_VALUES,
  type CtaClickedValue,
} from "./events/cta";
export {
  ENTRYPOINT_VIEWED_VALUES,
  type EntrypointViewedValue,
} from "./events/entrypoint";
export {
  ERROR_SHOWN_VALUES,
  type ErrorShownValue,
} from "./events/error";
export {
  NPS_COMPLETED_VALUES,
  type NpsCompletedValue,
} from "./events/misc";
export {
  NIGHTWATCH_HARD_STOP_VALUES,
  type NightwatchHardStopValue,
} from "./events/nightwatch";
export {
  MDI_INTAKE_STARTED_VALUES,
  MDI_INTAKE_COMPLETED_VALUES,
  type MdiIntakeStartedValue,
  type MdiIntakeCompletedValue,
} from "./events/mdi";
export {
  CARE_PROFILE_COMPLETED_VALUES,
  type CareProfileCompletedValue,
} from "./events/profile";
export {
  VISIT_REQUEST_STARTED_VALUES,
  VISIT_CANCELLED_VALUES,
  type VisitRequestStartedValue,
  type VisitCancelledValue,
} from "./events/visit";

import { VISIT_CANCELLED_VALUES, type VisitCancelledValue } from "./events/visit";

/**
 * Screaming-snake aliases for event names. Every value is a valid
 * `RegisteredEventName` — enforced by the `satisfies` clause.
 */
export const STATSIG_EVENTS = {
  IMPRESSION: "entrypoint_viewed",
  BUTTON_PRESS: "cta_clicked",

  ONBOARDING_WELCOME_CONTINUE: "onboarding_welcome_continue",

  HIPAA_CONSENT_ACCEPTED: "hipaa_consent_accepted",

  TRIAGE_REQUEST_CONFIRMED: "triage_request_confirmed",
  TRIAGE_REQUEST_CANCELLED: "triage_request_cancelled",

  START_A_VISIT: "visit_request_started",
  VISIT_REQUEST_CANCELLED: "visit_request_cancelled",
  VISIT_COMPLETED: "visit_completed",
  VISIT_ACTIVE: "visit_active",

  PROFILE_SUBMITTED: "profile_submitted",
  PROFILE_CANCELLED: "profile_cancelled",
  PROFILE_COMPLETED: "care_profile_completed",

  ACCOUNT_CREATED: "account_created",
  ACCOUNT_EXISTING_RETURN: "account_existing_return",

  MANAGE_SUBSCRIPTION: "manage_subscription",
  TERMS_CONDITIONS: "terms_conditions",
  CONTACT_SUPPORT: "contact_support",
  SUB_CANCEL_REACTIVATE: "sub_cancel_reactivate",
  PAYMENT_ERR_MANAGE: "payment_err_manage",

  ERROR_SCREEN_SHOWN: "error_shown",

  MDI_QUESTIONNAIRE_LOADED: "mdi_questionnaire_loaded",
  MDI_QUESTIONNAIRE_STARTED: "mdi_intake_started",
  MDI_QUESTIONNAIRE_FINISHED: "mdi_intake_completed",
  MDI_QUESTIONNAIRE_UNLOAD: "mdi_questionnaire_unload",
  MDI_QUESTIONNAIRE_STEP: "mdi_questionnaire_step",
  MDI_ENCOUNTER_CREATED: "mdi_encounter_created",
  MDI_PATIENT_CREATED: "mdi_patient_created",
  MDI_QUESTION_SKIPPED: "mdi_question_skipped",
  MDI_DEAD_END_QUESTION: "mdi_dead_end_question",
  MDI_PATIENT_LOGGED_IN: "mdi_patient_logged_in",

  SHORTLINK_FOLLOWED: "shortlink_followed",

  NPS_COMPLETED: "nps_completed",

  NIGHTWATCH_ARMED: "nightwatch_armed",
  NIGHTWATCH_INTAKE_HARD_STOP: "nightwatch_intake_hard_stop",

  UI_DISMISSED: "ui_dismissed",
  PAYWALL_VIEWED: "paywall_viewed",
  CHECKOUT_VIEWED: "checkout_viewed",
  PAYMENT_SUBMITTED: "payment_submitted",
  PAYMENT_SUCCEEDED: "payment_succeeded",
  CONDITION_SELECTED: "condition_selected",

  SUBSCRIPTION_CREATED: "subscription_created",
  SUBSCRIPTION_STATUS_UPDATED: "subscription_status_updated",
  SUBSCRIPTION_CANCELED: "subscription_canceled",
  SUBSCRIPTION_REVENUE_RECOGNIZED: "subscription_revenue_recognized",

  CASE_CREATED: "case_created",
  VISIT_PROVIDER_ASSIGNED: "visit_provider_assigned",
  VISIT_CANCELLED: "visit_cancelled",

  // v2 additions
  BILLING_CADENCE_SWITCHED: "billing_cadence_switched",
  PRODUCT_CARD_SELECTED: "product_card_selected",
  CHECKOUT_METHOD_LOADED: "checkout_method_loaded",
  CHECKOUT_ABANDONED: "checkout_abandoned",
  CHILD_SELECTOR: "child_selector",
  CHAT_MESSAGE_SENT: "chat_message_sent",
  CHAT_FIRST_RESPONSE_RECEIVED: "chat_first_response_received",
  CANCEL_INTENT_SET: "cancel_intent_set",
  CANCEL_INTENT_REVERSED: "cancel_intent_reversed",
  SUBSCRIPTION_PAYMENT_FAILED: "subscription_payment_failed",
  SUBSCRIPTION_PAYMENT_RECOVERED: "subscription_payment_recovered",
  SUBSCRIPTION_REFUNDED: "subscription_refunded",
  SUBSCRIPTION_CHARGE_DISPUTED: "subscription_charge_disputed",
  TRIAL_ENDED: "trial_ended",
  SUBSCRIPTION_CANCELED_FEEDBACK: "subscription_canceled_feedback",
} as const satisfies Record<string, RegisteredEventName>;

export type StatsigEventName =
  (typeof STATSIG_EVENTS)[keyof typeof STATSIG_EVENTS];

/**
 * Map an MDI cancel-reason string to a taxonomy-compliant VisitCancelledValue.
 * Preserved unchanged from v1 for backwards compatibility.
 */
export function mapMdiCancelReason(mdiReason: string): VisitCancelledValue {
  const normalized = mdiReason.toLowerCase().trim();

  if (normalized.includes("patient") || normalized.includes("user")) {
    return VISIT_CANCELLED_VALUES.PATIENT_REQUEST;
  }
  if (normalized.includes("provider") || normalized.includes("clinician")) {
    return VISIT_CANCELLED_VALUES.PROVIDER_UNAVAILABLE;
  }
  if (normalized.includes("duplicate")) {
    return VISIT_CANCELLED_VALUES.DUPLICATE_CASE;
  }
  if (normalized.includes("scope") || normalized.includes("ineligible")) {
    return VISIT_CANCELLED_VALUES.OUT_OF_SCOPE;
  }
  if (normalized.includes("technical") || normalized.includes("error")) {
    return VISIT_CANCELLED_VALUES.TECHNICAL_ERROR;
  }
  if (normalized.includes("timeout") || normalized.includes("expired")) {
    return VISIT_CANCELLED_VALUES.TIMEOUT;
  }

  return VISIT_CANCELLED_VALUES.OTHER;
}

/**
 * Construct a `{ eventName, metadata }` payload. Preserved from v1; new
 * code should prefer `logEvent` directly with the typed registry.
 *
 * `metadata: Record<string, any>` matches the Statsig SDK's `StatsigEvent`
 * shape (`Record<string, string | undefined>`) loosely so existing
 * callers compile unchanged. Strict typing happens via the registry +
 * runtime validation, not via this helper's signature.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createStatsigEvent(
  eventName: StatsigEventName,
  metadata: Record<string, any> = {}
) {
  return {
    eventName,
    metadata,
  };
}
